import React, { useCallback, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { IoCloseOutline } from "react-icons/io5";
import { IoMdNotificationsOutline } from "react-icons/io";
import { useNotifications } from "../../context/NotificationsContext/NotificationsProvider";
import Loader from "../icons/Loader";

const timeAgo = (iso) => {
  if (!iso) return "";
  const relative = DateTime.fromISO(iso).toRelative({ style: "short" });
  return relative || "";
};

const DRAWER_ANIMATION_MS = 280;
const ITEM_EXIT_MS = 220;

const NotificationItem = ({
  notification,
  onOpen,
  onDismiss,
  index = 0,
  exiting = false,
}) => {
  const unread = !notification.readAt;
  const batchCount = notification.metadata?.batchCount || notification.batchCount;

  return (
    <div
      onClick={() => {
        if (exiting) return;
        onOpen(notification);
      }}
      className={`group relative flex cursor-pointer items-start gap-kit-12 overflow-hidden px-kit-20 py-kit-16 border-b-[1px] border-[var(--inyc-input-border-color)] transition-colors duration-150 hover:bg-[var(--inyc-primary-light)] ${
        unread ? "bg-[var(--inyc-primary-light)]" : ""
      } ${exiting ? "pointer-events-none" : ""}`}
      style={{
        animation: exiting
          ? `notification-item-out ${ITEM_EXIT_MS}ms ease-in forwards`
          : `notification-item-in 220ms ease-out both`,
        animationDelay: exiting ? "0ms" : `${Math.min(index, 8) * 24}ms`,
      }}
    >
      {/* unread indicator */}
      <div className="pt-kit-8">
        <span
          className={`block w-[0.5rem] h-[0.5rem] rounded-kit-full ${
            unread ? "bg-badge" : "bg-transparent"
          }`}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div
          className={`text-kit-base leading-snug ${
            unread
              ? "font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]"
              : "text-[var(--inyc-primary-text-color)]"
          }`}
        >
          {notification.title}
          {batchCount > 1 && (
            <span className="ml-kit-6 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
              ×{batchCount}
            </span>
          )}
        </div>
        {notification.body && (
          <div className="mt-kit-2 text-kit-sm leading-snug text-[var(--inyc-primary-text-color-light)] line-clamp-2">
            {notification.body}
          </div>
        )}
        <div className="mt-kit-4 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
          {timeAgo(notification.createdAt)}
        </div>
      </div>

      <button
        type="button"
        aria-label="dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          if (exiting) return;
          onDismiss(notification._id);
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-transparent border-0 p-kit-4 cursor-pointer"
      >
        <IoCloseOutline
          size={18}
          color="var(--inyc-primary-text-color-light)"
        />
      </button>
    </div>
  );
};

/**
 * Global notification drawer. Mounted once at app root next to the provider.
 */
const NotificationDrawer = () => {
  const {
    drawerOpen,
    closeDrawer,
    items,
    hasMore,
    feedLoading,
    feedLoaded,
    loadFeed,
    markAllRead,
    openNotification,
    dismissNotification,
    counts,
  } = useNotifications();
  const [shouldRender, setShouldRender] = useState(false);
  const [entered, setEntered] = useState(false);
  const [exitingIds, setExitingIds] = useState(() => new Set());
  const exitTimersRef = useRef(new Map());

  const handleDismiss = useCallback(
    (id) => {
      const key = String(id);
      if (exitTimersRef.current.has(key)) return;

      setExitingIds((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      const timeout = setTimeout(() => {
        dismissNotification(id);
        setExitingIds((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        exitTimersRef.current.delete(key);
      }, ITEM_EXIT_MS);
      exitTimersRef.current.set(key, timeout);
    },
    [dismissNotification]
  );

  useEffect(
    () => () => {
      exitTimersRef.current.forEach((timeout) => clearTimeout(timeout));
      exitTimersRef.current.clear();
    },
    []
  );

  useEffect(() => {
    if (drawerOpen) {
      setShouldRender(true);
      setEntered(false);
      // first paint must be the closed state, then flip to open so CSS can animate in
      let innerFrame;
      const frame = requestAnimationFrame(() => {
        innerFrame = requestAnimationFrame(() => setEntered(true));
      });
      return () => {
        cancelAnimationFrame(frame);
        if (innerFrame) cancelAnimationFrame(innerFrame);
      };
    }

    setEntered(false);
    const timeout = setTimeout(() => {
      setShouldRender(false);
    }, DRAWER_ANIMATION_MS);

    return () => clearTimeout(timeout);
  }, [drawerOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-[100000] transition-opacity duration-300 ease-out ${
        entered ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      {/* backdrop */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ease-out ${
          entered ? "opacity-100" : "opacity-0"
        }`}
        onClick={closeDrawer}
      />

      {/* panel */}
      <aside
        className={`absolute right-0 top-0 flex h-full w-full max-w-[420px] flex-col bg-[var(--inyc-primary)] shadow-2xl pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          entered ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-kit-20 py-kit-16 border-b-[1px] border-[var(--inyc-input-border-color)]">
          <div className="text-kit-lg font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
            notifications
          </div>
          <div className="flex items-center gap-kit-16">
            {counts.total > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="bg-transparent border-0 cursor-pointer text-kit-xs text-[var(--inyc-primary-text-color-light)] underline hover:text-[var(--inyc-primary-text-color)]"
              >
                mark all read
              </button>
            )}
            <button
              type="button"
              aria-label="close notifications"
              onClick={closeDrawer}
              className="bg-transparent border-0 p-0 cursor-pointer"
            >
              <IoCloseOutline
                size={26}
                color="var(--inyc-primary-text-color)"
              />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!feedLoaded && feedLoading && (
            <div className="flex justify-center py-kit-48">
              <Loader />
            </div>
          )}

          {feedLoaded && items.length === 0 && (
            <div className="flex flex-col items-center gap-kit-12 py-kit-64 px-kit-32 text-center">
              <IoMdNotificationsOutline
                size={40}
                color="var(--inyc-primary-text-color-light)"
              />
              <div className="text-kit-md text-[var(--inyc-primary-text-color-light)]">
                you're all caught up
              </div>
            </div>
          )}

          {items.map((notification, index) => (
            <NotificationItem
              key={notification._id}
              notification={notification}
              index={index}
              exiting={exitingIds.has(String(notification._id))}
              onOpen={openNotification}
              onDismiss={handleDismiss}
            />
          ))}

          {feedLoaded && hasMore && (
            <button
              type="button"
              disabled={feedLoading}
              onClick={() => loadFeed()}
              className="w-full bg-transparent border-0 cursor-pointer py-kit-20 text-kit-sm text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)]"
            >
              {feedLoading ? "loading..." : "load more"}
            </button>
          )}
        </div>
      </aside>
      <style>{`
        @keyframes notification-item-in {
          from {
            opacity: 0;
            transform: translateX(12px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes notification-item-out {
          from {
            opacity: 1;
            transform: translateX(0);
            max-height: 12rem;
          }
          to {
            opacity: 0;
            transform: translateX(40%);
            max-height: 0;
            padding-top: 0;
            padding-bottom: 0;
            border-bottom-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationDrawer;
